import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button, Empty, Space, Spin, Table, Tabs, message } from "antd";
import { ArrowLeftOutlined, PrinterOutlined, ReloadOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency, toApiNumber } from "../../../utils/formatters";
import usePermissions from "../../../hooks/usePermissions";
import "../../../shared/styles/ErpPages.css";

const isCancelled = (o) => o?.status === "cancelled" || Number(o?.payment_status) === 4;

const statusClass = (o) => {
  if (isCancelled(o)) return "cancelled";
  const due = toApiNumber(o.outstanding_balance, 0);
  if (due <= 0.0001) return "paid";
  if (toApiNumber(o.amount_paid, 0) > 0) return "partial";
  return "unpaid";
};

const statusLabel = (o, t) => {
  const c = statusClass(o);
  if (c === "cancelled") return t("cancelled", "Cancelled");
  if (c === "paid") return t("paid", "Paid");
  if (c === "partial") return t("partial", "Partial");
  return t("unpaid", "Unpaid");
};

/**
 * ERP customer master — header + tabs (Overview / Sales / Payments).
 * Print documents are separate routes and are not restyled here.
 */
const CustomerWorkspace = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [salesFilter, setSalesFilter] = useState("active");
  const [info, setInfo] = useState(null);
  const [orders, setOrders] = useState([]);
  const [payments, setPayments] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [salesRes, payRes] = await Promise.all([
        apiClient.get(`/customers/${id}/orders`),
        apiClient.get(`/customers/${id}/payments`),
      ]);
      setInfo(salesRes.data || null);
      setOrders(Array.isArray(salesRes.data?.orders) ? salesRes.data.orders : []);
      setPayments(Array.isArray(payRes.data?.payments) ? payRes.data.payments : []);
    } catch (e) {
      message.error(e?.response?.data?.message || t("loadError", "Failed to load"));
      setInfo(null);
      setOrders([]);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, [id, t]);

  useEffect(() => {
    load();
  }, [load]);

  const owed = toApiNumber(info?.total_owed, 0);
  const activeOrders = useMemo(() => orders.filter((o) => !isCancelled(o)), [orders]);
  const cancelledOrders = useMemo(() => orders.filter(isCancelled), [orders]);
  const unpaidOrders = useMemo(
    () => activeOrders.filter((o) => toApiNumber(o.outstanding_balance, 0) > 0.0001),
    [activeOrders]
  );
  const normalPayments = useMemo(
    () => payments.filter((p) => p.payment_method !== "cancel_refund" && toApiNumber(p.amount, 0) >= 0),
    [payments]
  );
  const refundPayments = useMemo(
    () => payments.filter((p) => p.payment_method === "cancel_refund" || toApiNumber(p.amount, 0) < 0),
    [payments]
  );

  const visibleSales = useMemo(() => {
    if (salesFilter === "unpaid") return unpaidOrders;
    if (salesFilter === "cancelled") return cancelledOrders;
    if (salesFilter === "all") return orders;
    return activeOrders;
  }, [salesFilter, unpaidOrders, cancelledOrders, orders, activeOrders]);

  if (!id) return <Empty description="Missing customer" />;

  if (loading) {
    return (
      <div className="erp-page" style={{ textAlign: "center", paddingTop: 80 }}>
        <Spin />
      </div>
    );
  }

  if (!info) {
    return (
      <div className="erp-page">
        <Empty description={t("noDataFound", "Customer not found")} />
        <Button onClick={() => navigate("/GetCustomer")}>{t("back", "Back")}</Button>
      </div>
    );
  }

  const salesColumns = [
    {
      title: t("date", "Date"),
      dataIndex: "created_at",
      width: 130,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—"),
    },
    { title: t("docNo", "Doc #"), dataIndex: "id", width: 80 },
    {
      title: t("status", "Status"),
      width: 100,
      render: (_, r) => <span className={`erp-status ${statusClass(r)}`}>{statusLabel(r, t)}</span>,
    },
    {
      title: t("total", "Total"),
      dataIndex: "total_price",
      align: "right",
      render: (v, r) => (
        <span className="erp-money" style={{ textDecoration: isCancelled(r) ? "line-through" : "none" }}>
          {formatCurrency(v)}
        </span>
      ),
    },
    {
      title: t("paid", "Paid"),
      dataIndex: "amount_paid",
      align: "right",
      render: (v) => <span className="erp-money">{formatCurrency(v)}</span>,
    },
    {
      title: t("balance", "Balance"),
      dataIndex: "outstanding_balance",
      align: "right",
      render: (v, r) => {
        if (isCancelled(r)) return <span className="erp-money muted">—</span>;
        const due = toApiNumber(v, 0);
        return <span className={`erp-money ${due > 0 ? "debt" : "ok"}`}>{formatCurrency(due)}</span>;
      },
    },
    {
      title: t("action", "Action"),
      width: 160,
      render: (_, r) => (
        <Space size={4}>
          <Button size="small" onClick={() => navigate(`/customer/${id}/order/${r.id}`)}>
            {t("open", "Open")}
          </Button>
          <Link to={`/TableOrder/InfoOrder/${r.id}`} target="_blank" rel="noreferrer">
            <Button size="small" icon={<PrinterOutlined />}>
              {t("print", "Print")}
            </Button>
          </Link>
        </Space>
      ),
    },
  ];

  const paymentColumns = [
    {
      title: t("date", "Date"),
      dataIndex: "created_at",
      width: 110,
      render: (v) => (v ? dayjs(v).format("DD/MM/YYYY") : "—"),
    },
    {
      title: t("type", "Type"),
      width: 130,
      render: (_, r) => {
        const amt = toApiNumber(r.amount, 0);
        if (r.payment_method === "cancel_refund" || amt < 0) {
          return <span className="erp-status cancelled">{t("cancelRefund", "Cancel refund")}</span>;
        }
        return <span className="erp-status paid">{t("receipt", "Receipt")}</span>;
      },
    },
    {
      title: t("amount", "Amount"),
      dataIndex: "amount",
      align: "right",
      render: (v) => {
        const amt = toApiNumber(v, 0);
        return <span className={`erp-money ${amt < 0 ? "debt" : "ok"}`}>{formatCurrency(amt)}</span>;
      },
    },
    {
      title: t("method", "Method"),
      dataIndex: "payment_method",
      render: (v) => (v === "cancel_refund" ? "—" : v || "—"),
    },
    {
      title: t("action", "Action"),
      width: 100,
      render: (_, r) => (
        <Link to={`/GetCustomer/PaymentRecipt/${id}/${r.id}`} target="_blank" rel="noreferrer">
          <Button size="small" icon={<PrinterOutlined />}>
            {t("print", "Print")}
          </Button>
        </Link>
      ),
    },
  ];

  const overview = (
    <div>
      <div className="erp-kv" style={{ marginBottom: 16 }}>
        <div className="k">{t("customer", "Customer")}</div>
        <div className="v">{info.name}</div>
        <div className="k">{t("phoneNumber", "Phone")}</div>
        <div className="v">{info.phone_number || "—"}</div>
        <div className="k">{t("type", "Type")}</div>
        <div className="v">{info.type || "—"}</div>
        <div className="k">{t("openBalance", "Open balance")}</div>
        <div className="v">
          <span className={`erp-money ${owed > 0 ? "debt" : "ok"}`}>{formatCurrency(owed)}</span>
        </div>
        <div className="k">{t("activeSales", "Active sales")}</div>
        <div className="v">
          {activeOrders.length} ({t("unpaid", "Unpaid")}: {unpaidOrders.length})
        </div>
        <div className="k">{t("cancelledSales", "Cancelled sales")}</div>
        <div className="v">{cancelledOrders.length}</div>
        <div className="k">{t("paymentEntries", "Payment entries")}</div>
        <div className="v">
          {normalPayments.length} / {t("refunds", "Refunds")}: {refundPayments.length}
        </div>
      </div>

      <div className="erp-actions-row">
        <Button type="primary" onClick={() => setTab("sales")}>
          {t("salesLedger", "Sales ledger")}
        </Button>
        <Button onClick={() => setTab("payments")}>{t("paymentLedger", "Payment ledger")}</Button>
        {hasPermission("customers.payment") && (
          <Button onClick={() => navigate(`/GetCustomer/Payment/${id}`)}>
            {t("receivePayment", "Receive payment")}
          </Button>
        )}
        {hasPermission("customers.payment") && (
          <Button onClick={() => navigate(`/GetCustomer/IncreasePayment/${id}`)}>
            {t("adjustDebt", "Adjust debt")}
          </Button>
        )}
        {hasPermission("customers.edit") && (
          <Button onClick={() => navigate(`/GetCustomer/UpdateCustomer/${id}`)}>
            {t("editMaster", "Edit master")}
          </Button>
        )}
      </div>
    </div>
  );

  const salesTab = (
    <div>
      <div className="erp-actions-row">
        {[
          ["active", t("active", "Active"), activeOrders.length],
          ["unpaid", t("unpaid", "Unpaid"), unpaidOrders.length],
          ["cancelled", t("cancelled", "Cancelled"), cancelledOrders.length],
          ["all", t("all", "All"), orders.length],
        ].map(([key, label, count]) => (
          <Button key={key} size="small" type={salesFilter === key ? "primary" : "default"} onClick={() => setSalesFilter(key)}>
            {label} ({count})
          </Button>
        ))}
      </div>
      <Table
        size="small"
        rowKey="id"
        dataSource={visibleSales}
        columns={salesColumns}
        pagination={{ pageSize: 25, showTotal: (n) => `${n} ${t("records", "records")}` }}
        locale={{ emptyText: t("noDataFound", "No records") }}
        scroll={{ x: true }}
      />
    </div>
  );

  const paymentsTab = (
    <div>
      <div className="erp-meta" style={{ border: "1px solid #e2e8f0", marginBottom: 12 }}>
        <span>
          {t("receiptsTotal", "Receipts")}:{" "}
          <b className="erp-money ok">
            {formatCurrency(normalPayments.reduce((s, p) => s + Math.max(0, toApiNumber(p.amount, 0)), 0))}
          </b>
        </span>
        <span>
          {t("refundsTotal", "Refunds")}:{" "}
          <b className="erp-money debt">
            {formatCurrency(Math.abs(refundPayments.reduce((s, p) => s + toApiNumber(p.amount, 0), 0)))}
          </b>
        </span>
      </div>
      <Table
        size="small"
        rowKey="id"
        dataSource={payments}
        columns={paymentColumns}
        pagination={{ pageSize: 25, showTotal: (n) => `${n} ${t("records", "records")}` }}
        locale={{ emptyText: t("noDataFound", "No records") }}
        scroll={{ x: true }}
      />
    </div>
  );

  return (
    <div className="erp-page">
      <div className="erp-shell">
        <div className="erp-header">
          <div>
            <div className="erp-breadcrumb">
              <Button type="link" size="small" icon={<ArrowLeftOutlined />} style={{ padding: 0 }} onClick={() => navigate("/GetCustomer")}>
                {t("customers", "Customers")}
              </Button>
              {" / "}
              {info.name}
            </div>
            <h1>
              {info.name}{" "}
              <span className={`erp-status ${owed > 0 ? "debt" : "clear"}`}>
                {owed > 0 ? t("open", "Open") : t("settled", "Settled")}
              </span>
            </h1>
          </div>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={load}>
              {t("refresh", "Refresh")}
            </Button>
            {hasPermission("customers.payment") && owed > 0 && (
              <Button type="primary" onClick={() => navigate(`/GetCustomer/Payment/${id}`)}>
                {t("receivePayment", "Receive payment")}
              </Button>
            )}
          </Space>
        </div>

        <div className="erp-meta">
          <span>
            {t("phoneNumber", "Phone")}: <b>{info.phone_number || "—"}</b>
          </span>
          <span>
            {t("openBalance", "Open balance")}:{" "}
            <b className={`erp-money ${owed > 0 ? "debt" : "ok"}`}>{formatCurrency(owed)}</b>
          </span>
          <span>
            {t("sales", "Sales")}: <b>{orders.length}</b>
          </span>
          <span>
            {t("payments", "Payments")}: <b>{payments.length}</b>
          </span>
        </div>

        <Tabs
          activeKey={tab}
          onChange={setTab}
          items={[
            { key: "overview", label: t("overview", "Overview"), children: overview },
            { key: "sales", label: `${t("sales", "Sales")} (${orders.length})`, children: salesTab },
            { key: "payments", label: `${t("payments", "Payments")} (${payments.length})`, children: paymentsTab },
          ]}
        />
      </div>
    </div>
  );
};

export default CustomerWorkspace;
